import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import QRCodeSVG from "qrcode.react";
import {reversedFlatCode, isMobile} from "../../../../../../components/commonmethod/CommonMethod";
import {
    buildCommonInfoFieldDefs,
    buildMaintenanceFieldDefs,
    chunkFieldsIntoRows,
    CommonInfoPanel,
    fieldsToKvCells,
    MaintenancePanel,
} from "../../shared/assetDetailKv";
import {
    ContentDetailTwoColLayout,
    DetailFilePanel,
    DetailListPage,
    DetailSummaryBar,
    DetailSummaryButton,
    DetailSummaryListButton,
    useRegisterDetailHeroSummary,
} from "../../../../../../components/detailLayout";
import DiligenceAssetQuickHandleModal from "../../../diligence/subPages/view/DiligenceAssetQuickHandleModal";
import "./assetInfo.css";

function AssetInfo({asset, flat, fields, customCodes, customStrings}) {
    const navigate = useNavigate();
    const platFrom = useSelector((state) => state.auth.platFrom);
    const origin = window.location.origin;
    const qrUrl = `${origin}/asset/${asset.id}`;

    const [fieldList, setFieldList] = useState([]);
    const [qrOpen, setQrOpen] = useState(false);
    const [diligenceModalOpen, setDiligenceModalOpen] = useState(false);
    const [diligenceProcessed, setDiligenceProcessed] = useState(false);

    useEffect(() => {
        if (!qrOpen) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [qrOpen]);

    useEffect(() => {
        if (!asset?.group?.id || !flat?.length) return;
        const matchedCode = flat.find((each) => each.id === asset.group.id);
        const codes = reversedFlatCode(matchedCode, flat);
        setFieldList(fields.filter((each) => codes.includes(each.code.id)));
    }, [asset?.group?.id, fields, flat]);

    const commonInfoRows = useMemo(() => {
        const defs = buildCommonInfoFieldDefs(asset, platFrom, customCodes, customStrings);
        return chunkFieldsIntoRows(defs).map((rowDefs, index) => {
            const cells = fieldsToKvCells(rowDefs, false, null);
            const key = rowDefs.map((d) => d.editArgs?.[0]?.id ?? d.editKey ?? d.label).join("-");
            return {key: key || `common-row-${index}`, cells};
        });
    }, [asset, platFrom, customCodes, customStrings]);

    const maintenanceRows = useMemo(() => {
        const defs = buildMaintenanceFieldDefs(asset, fieldList);
        return chunkFieldsIntoRows(defs).map((rowDefs, index) => {
            const cells = fieldsToKvCells(rowDefs, false, null);
            const key = rowDefs.map((d) => d.editArgs?.[0]?.id ?? d.editKey ?? d.label).join("-");
            return {
                key: key || `maint-row-${index}`,
                cells,
                rowAuto: rowDefs.some((d) => d.rowAuto),
            };
        });
    }, [asset, fieldList]);

    const summaryMeta = [
        asset.assetCode && `코드 ${asset.assetCode}`,
        asset.group?.name && `그룹 ${asset.group.name}`,
        asset.location?.name && `위치 ${asset.location.name}`,
    ]
        .filter(Boolean)
        .join(" · ");

    const summaryActions = (
        <>
            <DetailSummaryListButton url="/asset/list" />
            <DetailSummaryButton
                disabled={!asset.auth?.update}
                onClick={() => navigate(`/asset/${asset.id}/edit`)}
            >
                수정
            </DetailSummaryButton>
            <button
                type="button"
                className="detail-list-summary-qr asset-detail-summary-qr"
                onClick={() => setQrOpen(true)}
                title="QR 코드 보기"
                aria-label="QR 코드 보기"
            >
                <QRCodeSVG value={qrUrl} size={24} bgColor="#FFFFFF" fgColor="#000000" />
            </button>
        </>
    );

    const {hideInlineSummaryBar} = useRegisterDetailHeroSummary({
        title: asset.assetName || "자산상세",
        meta: summaryMeta || undefined,
        actions: summaryActions,
    });

    const diligenceId = asset?.auth?.link;
    const isInDiligence =
        !diligenceProcessed && diligenceId != null && diligenceId !== 0 && diligenceId !== "0";

    useEffect(() => {
        setDiligenceProcessed(false);
    }, [diligenceId]);

    return (
        <DetailListPage isEditing={false} className="flex min-h-0 flex-1 flex-col">
            {!hideInlineSummaryBar ? (
                <DetailSummaryBar
                    title={asset.assetName || "자산상세"}
                    meta={summaryMeta || undefined}
                    actions={summaryActions}
                />
            ) : null}

            {isInDiligence ? (
                <div className="detail-list-top-slot asset-diligence-banner-slot">
                    <div
                        className="asset-diligence-banner"
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/diligence/${diligenceId}/handle`)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                navigate(`/diligence/${diligenceId}/handle`);
                            }
                        }}
                    >
                        <span className="asset-diligence-banner__icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M12 8v5m0 3h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        <span className="asset-diligence-banner__text">현재 실사 진행 중인 자산입니다</span>
                        <span className="asset-diligence-banner__action">실사 처리</span>
                    </div>
                </div>
            ) : null}

            <DiligenceAssetQuickHandleModal
                open={diligenceModalOpen}
                onClose={() => setDiligenceModalOpen(false)}
                diligenceId={diligenceId}
                asset={asset}
                onProcessed={() => setDiligenceProcessed(true)}
            />

            {qrOpen && (
                <div className="asset-detail-qr-modal-root">
                    <div
                        className="image-view-modal-background"
                        onClick={() => setQrOpen(false)}
                        aria-hidden
                    />
                    <div className="asset-detail-qr-modal" role="dialog" aria-modal="true" aria-label="QR 코드">
                        <QRCodeSVG value={qrUrl} size={220} bgColor="#FFFFFF" fgColor="#000000" />
                        <div className="asset-detail-qr-modal-caption">{asset.assetName || "자산상세"}</div>
                    </div>
                </div>
            )}

            <div className={`container-height-body asset-detail-body detail-list-body flex min-h-0 flex-1 flex-col ${isMobile ? "" : "overflow-hidden"}`}>
                <ContentDetailTwoColLayout
                    main={
                        <>
                            <CommonInfoPanel rows={commonInfoRows} isEditing={false} />
                            <MaintenancePanel rows={maintenanceRows} isEditing={false} />
                        </>
                    }
                    aside={
                        <DetailFilePanel
                            title="파일"
                            isEditing={false}
                            tableInfo={{tableName: "asset", joinKey: asset.id}}
                        />
                    }
                />
            </div>
        </DetailListPage>
    );
}

export default AssetInfo;
